import { type GenericMap } from "@common/declarations";
import { type Property } from "csstype";
import escapeHtml from "escape-html";
import { trim } from "lodash";
import { type Descendant, Text } from "slate";
import { jsx } from "slate-hyperscript";
import { type ElementBase, type ImageElement, type LinkElement, type RichEditorMode, type SlateEditor, type VideoElement } from "./types";

const serialize = (node: any, mode: RichEditorMode | undefined): string => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (mode !== "singleLinePlain") {
      if (node.bold) {
        string = `<strong>${string}</strong>`;
      }
      if (node.italic) {
        string = `<em>${string}</em>`;
      }
      if (node.strikethrough) {
        string = `<s>${string}</s>`;
      }
      if (node.underline) {
        string = `<u>${string}</u>`;
      }
      if (node.color) {
        string = `<span style="color: ${node.color}">${string}</span>`;
      }
    }
    return string;
  }

  const children = node.children.map((n) => serialize(n, mode)).join("");

  if (mode === "singleLinePlain") return children;

  const style: string = node.align ? ` style="text-align: ${node.align}"` : "";

  switch (node.type) {
    case "p":
      return `<p${style}>${children}</p>`;
    case "a": {
      const a = node as LinkElement;
      return `<a${style} href="${escapeHtml(a.href)}"${a.target ? ` target=${a.target}` : ""}>${children}</a>`;
    }
    case "list-item":
      return `<li${style}>${children}</li>`;
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
    case "ol":
    case "ul":
      return `<${node.type}${style}>${children}</${node.type}>`;
    case "image": {
      const { url, mediaId } = node as ImageElement;
      return `<img src="${escapeHtml(url)}" data-media-id="${mediaId ?? ""}" />`;
    }
    case "video": {
      const { mediaId, mediaSourceId, mediaSourceKey, aspectRatio } = node as VideoElement;
      return `<video data-media-id="${mediaId ?? ""}" data-media-source-id="${
        mediaSourceId ?? ""
      }" data-media-source-key="${mediaSourceKey ?? ""}" data-aspect-ratio="${aspectRatio}" ></video>`;
    }
    default: {
      return children;
    }
  }
};

const deserialize = (el: HTMLElement, markAttributes: GenericMap<any> = {}) => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx("text", markAttributes, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const nodeAttributes = { ...markAttributes };

  // define attributes for text nodes
  switch (el.nodeName) {
    case "EM": {
      nodeAttributes.italic = true;
      break;
    }
    case "S": {
      nodeAttributes.strikethrough = true;
      break;
    }
    case "STRONG": {
      nodeAttributes.bold = true;
      break;
    }
    case "U": {
      nodeAttributes.underline = true;
      break;
    }
    case "SPAN": {
      nodeAttributes.color = el.style.color;
      break;
    }
    default: {
      break;
    }
  }
  const children = Array.from(el.childNodes)
    .map((node) => deserialize(node as HTMLElement, nodeAttributes))
    .flat();

  if (children.length === 0) {
    children.push(jsx("text", nodeAttributes, ""));
  }

  const baseAttributes: ElementBase = {
    align: el.style.textAlign as Property.TextAlign
  };
  switch (el.nodeName) {
    case "BODY":
      return jsx("fragment", {}, children);
    case "BLOCKQUOTE":
      return jsx("element", { ...baseAttributes, type: "quote" }, children);
    case "DIV":
    case "P":
      return jsx("element", { ...baseAttributes, type: "p" }, children);
    case "H1":
    case "H2":
    case "H3":
    case "H4":
    case "H5":
    case "H6":
    case "OL":
    case "UL":
      return jsx("element", { ...baseAttributes, type: el.nodeName.toLowerCase() }, children);
    case "A":
      return jsx(
        "element",
        { ...baseAttributes, type: "a", href: el.getAttribute("href"), target: el.getAttribute("target") },
        children
      );
    case "LI":
      return jsx("element", { ...baseAttributes, type: "list-item" }, children);
    case "IMG":
      return jsx(
        "element",
        {
          type: "image",
          url: el.getAttribute("src"),
          mediaId: el.dataset.mediaId
        },
        [{ text: "" }]
      );
    case "VIDEO":
      return jsx(
        "element",
        {
          type: "video",
          url: el.getAttribute("src"),
          mediaId: el.dataset.mediaId,
          mediaSourceId: el.dataset.mediaSourceId,
          mediaSourceKey: el.dataset.mediaSourceKey,
          aspectRatio: el.dataset.aspectRatio
        },
        [{ text: "1" }]
      );
    default:
      return children;
  }
};

export const fromHtml = (html: string | null): Descendant[] => {
  if (!html) return [{ type: "p", children: [{ text: "" }] }];

  if (!html.startsWith("<")) html = `<p>${html}</p>`;

  const document = new DOMParser().parseFromString(html, "text/html");
  return deserialize(document.body);
};

export const toHtml = (nodes: Descendant[] | SlateEditor, mode: RichEditorMode | undefined): string =>
  trim(serialize(Array.isArray(nodes) ? { children: nodes } : nodes, mode), "\n");
